import { z } from 'zod';

import { SpecType } from '../types';
import { QualitySpecSchema } from './QualityDto';

// ────────────────────────────────────────────────────────────────────────────
// Pagination helpers (inlined — matches the pattern used by other list DTOs)
// ────────────────────────────────────────────────────────────────────────────

const PaginationSchema = z.object({
  limit: z.number().int().min(1).max(1000).default(200),
  nextToken: z.string().optional().nullable(),
});

const PaginatedResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    count: z.number().int().min(0),
    items: z.array(itemSchema),
    nextToken: z.string().optional().nullable(),
  });

// ────────────────────────────────────────────────────────────────────────────
// Template
// ────────────────────────────────────────────────────────────────────────────

export type ITemplateConfig = z.infer<typeof QualitySpecSchema>;

export const TemplateSchema = z.object({
  config: QualitySpecSchema,
  createdAt: z.string(),
  description: z.string().optional(),
  id: z.string(),
  name: z.string().min(1),
  specType: z.enum(SpecType),
  updatedAt: z.string(),
});

export type ITemplate = z.infer<typeof TemplateSchema>;

// A template's `config` reuses QualitySpecSchema (the same formula shape the quality-adjustments
// calculate endpoint validates against) as-is, rather than a parallel, template-only copy — its
// formula shape and validation rules can never drift from what that endpoint actually accepts.
// `config.specType` is forced to match the outer `specType` (the sole source of truth for which
// quality attribute a template configures) rather than rejecting a mismatch, since the client has
// no legitimate reason to send a different value on purpose.
const syncConfigSpecType = <
  T extends { config: { specType: string }; specType: string },
>(
  data: T,
): T => ({ ...data, config: { ...data.config, specType: data.specType } });

export const TemplateCreateSchema = z
  .object({
    config: QualitySpecSchema,
    description: z.string().optional(),
    name: z.string().min(1),
    specType: z.enum(SpecType),
  })
  .transform(syncConfigSpecType);

export type ITemplateCreate = z.infer<typeof TemplateCreateSchema>;

export const TemplateUpdateSchema = TemplateCreateSchema;

export type ITemplateUpdate = z.infer<typeof TemplateUpdateSchema>;

export const TemplateListQuerySchema = PaginationSchema.extend({
  specType: z.enum(SpecType).optional(),
});

export type ITemplateListQuery = z.infer<typeof TemplateListQuerySchema>;

export const TemplateListResponseSchema =
  PaginatedResponseSchema(TemplateSchema);

export type ITemplateListResponse = z.infer<typeof TemplateListResponseSchema>;
